import React, { useEffect, useState } from 'react';
import useTranslation from '../../../../../Hooks/useTranslation';
import { useWorkplaceCaseTargetQuery } from '../../../../../graphql/graphql';
import { useAppContext } from '../../../../../Services/AppContext';
import PatientRegistrationFavoritePresetSelect, {
	createdByTypeOptions,
} from '../Utils/PatientRegistrationFavoritePresetSelect';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import InputField from '../../../../../Components/Form/components/InputField';
import Text from '../../../../../Components/Base/Text/Text';
// @ts-ignore
import styles from '../PatientManualRegistration.module.scss';
import FormContainer from '../../../../../Components/Form/FormContainer';
import CitizenshipSelectField from '../../../../Base/Components/Fields/Citizenship/CitizenshipSelectField';
import GenderSelectField from '../../../../Base/Components/Inputs/GenderSelect/GenderSelectField';
import PhoneNumberField from '../../../../../Components/PhoneNumber/PhoneNumberField';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import CheckboxField from '../../../../../Components/Form/components/CheckboxField';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import usePatient from '../../../../Patient/Hooks/usePatient';
import { SelectValue } from '../../../../../Components/Base/Select/Select';
import usePatientManualRegistrationPolandFormValidationScheme from '../../../Validation/PatientManualRegistrationPolandFormValidationScheme';
import PolandAddressContainer from '../../../../Base/Components/FiledsGroups/PolandAddressContainer';
import NumberField from '../../../../../Components/Form/components/NumberField';

export interface PatientManualRegistrationPolandFormValues {
	pesel: string;
	salutationBefore?: string;
	salutationAfter?: string;
	firstName: string;
	lastName: string;
	email: string;
	address: {
		street: string;
		descriptiveNumber: string;
		guideNumber?: string;
		city: string;
		postCode: string;
	};
	citizenship: SelectValue;
	gender: SelectValue;
	phoneNumber?: string;
	sendPatientInviteToMeddi: boolean;
	legalRepresentative?: {
		firstName: string;
		lastName: string;
		email: string;
		phoneNumber?: string;
	};
	createdByType: SelectValue;
	createdById?: {
		id?: string;
		label: string;
		item: {
			user: {
				id: string;
			};
		};
	};
}

interface Props {
	onSuccess: (values: PatientManualRegistrationPolandFormValues) => void;
}

const PatientManualRegistrationPolandForm: React.FC<Props> = ({
	onSuccess,
}) => {
	const { t } = useTranslation();
	const [isChild, setIsChild] = useState(false);
	const { validationSchema } =
		usePatientManualRegistrationPolandFormValidationScheme(isChild);
	const { workplaceId } = useAppContext();
	const { data: WorkplaceCaseTargetData } = useWorkplaceCaseTargetQuery({
		id: workplaceId,
	});
	const { checkIsAdultByInsuranceNumber } = usePatient();

	const defaultValues: any = {
		sendPatientInviteToMeddi: true,
	};

	const formMethods = useForm({
		defaultValues: defaultValues,
		resolver: yupResolver(validationSchema),
	});

	const phoneNumberValue = formMethods.watch('phoneNumber');
	const emailValue = formMethods.watch('email');

	const peselValue = formMethods.watch('pesel');

	const preselectCreatedByType = () => {
		const option = createdByTypeOptions.find(
			item =>
				item.value === WorkplaceCaseTargetData?.workplace?.createCaseTarget,
		);
		if (option) {
			formMethods.setValue('createdByType', {
				label: t(option.label),
				value: option.value,
			});
		}
	};

	const handleSuccess = (values: any) => {
		onSuccess(values as PatientManualRegistrationPolandFormValues);
	};

	useEffect(() => {
		preselectCreatedByType();
	}, [WorkplaceCaseTargetData]);

	useEffect(() => {
		if (peselValue && peselValue?.length >= 6) {
			setIsChild(!checkIsAdultByInsuranceNumber(peselValue));
		}
		if (peselValue?.length < 6) {
			setIsChild(false);
		}
	}, [peselValue]);

	return (
		<FormWrapper formMethods={formMethods}>
			<div className='v2-forms__gray-background'>
				<div className='row'>
					<div className='col-lg-4 col-12'>
						<NumberField
							name='pesel'
							label='base.pesel'
							charactersLimit={11}
							showCharCounter={false}
							isRequired
						/>
					</div>
				</div>
			</div>
			{!isChild && (
				<div className='row mb-3'>
					<div className='col-lg-6 col-12'>
						<InputField
							name='salutationBefore'
							label='base.shared.salutationBefore'
							charactersLimit={50}
							showCharCounter={false}
						/>
					</div>
					<div className='col-lg-6 col-12'>
						<InputField
							name='salutationAfter'
							label='base.shared.salutationAfter'
							charactersLimit={50}
							showCharCounter={false}
						/>
					</div>
				</div>
			)}
			<div className='row'>
				<div className='col-lg-6 col-12'>
					<InputField
						name='firstName'
						label='base.shared.firstName'
						isRequired
					/>
				</div>
				<div className='col-lg-6 col-12'>
					<InputField name='lastName' label='base.shared.lastName' isRequired />
				</div>
			</div>
			<Text className={styles.formText} tx='base.permanentAddress' firstUpper />
			<PolandAddressContainer />
			<Text
				className={styles.formText}
				tx='base.additionalInformations'
				firstUpper
			/>
			<div className='row mb-3'>
				<div className='col-lg-6 col-12'>
					<CitizenshipSelectField
						name='citizenship'
						label='base.citizenship'
						isRequired
					/>
				</div>
				<div className='col-lg-6 col-12'>
					<GenderSelectField
						valueKey='id'
						name='gender'
						label='base.shared.gender'
						isRequired
					/>
				</div>
			</div>
			{!isChild && (
				<div className='row mb-3'>
					<div className='col-lg-6 col-12'>
						<PhoneNumberField
							name='phoneNumber'
							label='base.shared.phone'
							isRequired={!emailValue}
						/>
					</div>
					<div className='col-lg-6 col-12'>
						<InputField
							name='email'
							label='base.shared.email'
							isRequired={!phoneNumberValue}
						/>
					</div>
				</div>
			)}
			<div className='mb-3'>
				<PatientRegistrationFavoritePresetSelect />
			</div>
			{isChild && (
				<>
					<Text
						className={styles.formText}
						tx='base.shared.legalRepresentative'
						firstUpper
					/>
					<FormContainer name={'legalRepresentative'}>
						<div className='row mb-3'>
							<div className='col-lg-6 col-12'>
								<InputField
									name='firstName'
									label='base.shared.firstName'
									isRequired
								/>
							</div>
							<div className='col-lg-6 col-12'>
								<InputField
									name='lastName'
									label='base.shared.lastName'
									isRequired
								/>
							</div>
						</div>
						<div className='row'>
							<div className='col-lg-6 col-12'>
								<PhoneNumberField
									name='phoneNumber'
									label='base.shared.phone'
								/>
							</div>
							<div className='col-lg-6 col-12'>
								<InputField name='email' label='base.shared.email' isRequired />
							</div>
						</div>
					</FormContainer>
				</>
			)}
			<div className='v2-forms__gray-background footer mt-3'>
				<div
					className='d-flex flex-row align-items-center'
					style={{ gap: '10px' }}>
					<div>
						<SubmitButton
							onSubmit={handleSuccess}
							title='base.registerThePatient'
						/>
					</div>
					<div className='pl-3' style={{ width: '350px' }}>
						<Text
							tx='base.emailWithInvitationWillArriveToPatientsEmail'
							className='v2-forms__light-text'
							firstUpper
						/>
						<CheckboxField
							name='sendPatientInviteToMeddi'
							tx='base.sendPatientInviteToMeddi'
							isRequired
							className='mt-2'
						/>
					</div>
				</div>
			</div>
		</FormWrapper>
	);
};

export default PatientManualRegistrationPolandForm;
